import {
  AloftArea,
  AloftDate,
  AloftDateJSON,
  AloftPrice,
  ListingsFiltersMatchCriteriaKey,
  RecommendedCompsStatistics,
  SubjectData,
  formatShortenedPropertyType,
} from '@aloft/toolkit-shared';
import { Block, Button, Drawer, Flex, Icon, Modal, Typography } from '@aloft/toolkit-sui';

import { useState } from 'react';

import { useZodFormContext } from '../../../../hooks/useZodForm.ts';
import { HeaderRow } from '../../../FilterDrawer/index.ts';
import { LoadingScreenSamInline } from '../../../LoadingScreen/LoadingScreenSamInline.tsx';
import { Table, Tbody, Thead } from '../../../table.tsx';
import { useSubjectCraftsmanPropertyToCompSelectionProperty } from '../../hooks/useSubjectCraftsmanPropertyToCompSelectionProperty.ts';
import { FiltersMessage, RecommendCompsFormSchema } from '../../types.ts';
import { FilterDrawerMapView } from './FilterDrawerMapView.tsx';
import { FilterRow } from './FilterRow.tsx';
import { FilterStatistics } from './FilterStatistics.tsx';

type Props = {
  subject: SubjectData;
  isOpen: boolean;
  effectiveDate: AloftDateJSON;
  isLoading: boolean;
  statistics: RecommendedCompsStatistics | undefined;
  filtersMessage?: FiltersMessage;
  allowApplyNewFilters: boolean;
  onCancel: () => void;
  onApply: () => void;
};

export const FilterDrawer = ({
  effectiveDate,
  isOpen,
  onCancel,
  onApply,
  subject,
  isLoading,
  statistics,
  filtersMessage,
  allowApplyNewFilters,
}: Props) => {
  const [isCloseWarningModalOpen, setIsCloseWarningModalOpen] = useState(false);
  const { trigger, setValue } = useZodFormContext<RecommendCompsFormSchema>();
  const effectiveDateAloft = AloftDate.fromIsoString(effectiveDate.isoString);
  const subjectComp = useSubjectCraftsmanPropertyToCompSelectionProperty(
    subject,
    effectiveDateAloft,
  );
  if (!isOpen) return null;

  const handleOnClose = () => {
    if (allowApplyNewFilters) {
      setIsCloseWarningModalOpen(true);
    } else {
      onCancel();
    }
  };

  const onSubmit = () => {
    const _ = trigger('search.filters').then((filterAreValid) => {
      if (filterAreValid) {
        onApply();
      }
    });
  };

  const propertyType = formatShortenedPropertyType(subject.editableData.improvements.propertyType);

  const subjectSalePrice = subject.editableData?.transaction.price
    ? AloftPrice.fromUnitAmount(
        subject.editableData.transaction.price.unitAmount,
        subject.editableData.transaction.price.currency,
      ).toString()
    : '-';
  const subjectGLA = subject.editableData?.improvements.gla
    ? `${AloftArea.fromNumber(
        subject.editableData?.improvements.gla.unitAmount,
        subject.editableData?.improvements.gla.unit,
      ).toSqFtValueStringRounded()} sqft`
    : '-';
  const subjectLotSize = subject.editableData?.site?.area
    ? `${AloftArea.fromNumber(
        subject.editableData?.site?.area.unitAmount,
        subject.editableData?.site?.area.unit,
      ).toSqFtValueStringRounded()} sqft`
    : '-';

  const clearFilter = (filterName: ListingsFiltersMatchCriteriaKey) => {
    setValue(`search.filters.${filterName}`, {
      max: '',
      min: '',
      exact: '',
      percentage: '',
      variance: '',
    });
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleOnClose}
      size="full"
      placement="bottom"
      header={<Typography variant="h1">Search Filters</Typography>}
      drawerContentProps={{
        backgroundColor: 'surface2',
        paddingX: '24px',
        paddingY: '22px',
      }}
      variant="overlay"
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <Modal
        isOpen={isCloseWarningModalOpen}
        onClose={onCancel}
        title="Unsaved Changes"
        primaryAction={{
          text: 'Apply & Close',
          onClick: () => {
            onApply();
            onCancel();
            setIsCloseWarningModalOpen(false);
          },
        }}
        secondaryAction={{
          text: 'Discard & Close',
          onClick: () => {
            onCancel();
            setIsCloseWarningModalOpen(false);
          },
        }}
        closeOnEsc={false}
        showCloseButton={false}
      >
        <Typography variant="body1">
          You&apos;ve updated the filters, but those changes haven&apos;t been applied yet. Do you
          want to apply them before closing?
        </Typography>
      </Modal>
      <Flex flexDirection="row" width="100%" height="100%" gap="24px">
        <Flex width="100%" height="100%" flexDirection="column" gap="24px">
          <Block>
            <Table>
              <Thead backgroundColor="surfaceAccent7">
                <HeaderRow includeMatchCriteria includeSubjectInfo />
              </Thead>
              <Tbody>
                <FilterRow
                  label="MLS Status"
                  subjectValue={subject.editableData.transaction.status ?? '-'}
                  fieldName="status"
                  inputType="select"
                  includeMatchCriteria={false}
                  includeClearButton={false}
                />
                {/* Shown to the user as property type, but this field internally represents property subtypes */}
                <FilterRow
                  label="Property Type"
                  subjectValue={propertyType}
                  fieldName="propertySubType"
                  inputType="select"
                  includeMatchCriteria={false}
                  includeClearButton={false}
                />
                <FilterRow
                  label="Sale Date"
                  subjectValue={subject.editableData.transaction.saleDate}
                  fieldName="saleDate"
                  inputType="date"
                  includeMatchCriteria
                  unitName="months (from Effective Date)"
                  includeClearButton
                  onClear={() => clearFilter('saleDate')}
                />
                <FilterRow
                  label="Sale Price"
                  subjectValue={subjectSalePrice}
                  fieldName="salePrice"
                  inputType="price"
                  includeMatchCriteria
                  includeClearButton
                  onClear={() => clearFilter('salePrice')}
                />
                <FilterRow
                  label="GLA"
                  subjectValue={subjectGLA}
                  fieldName="gla"
                  inputType="area"
                  includeMatchCriteria
                  includeClearButton
                  onClear={() => clearFilter('gla')}
                />
                <FilterRow
                  label="Lot Size"
                  subjectValue={subjectLotSize}
                  fieldName="lotSize"
                  inputType="area"
                  includeMatchCriteria
                  includeClearButton
                  onClear={() => clearFilter('lotSize')}
                />
                <FilterRow
                  label="Year Built"
                  subjectValue={subject?.readonlyData?.backupListingData?.listing?.YearBuilt?.toString()}
                  fieldName="yearBuilt"
                  inputType="unit"
                  includeMatchCriteria
                  unitName="years"
                  includeClearButton
                  onClear={() => clearFilter('yearBuilt')}
                />
                <FilterRow
                  label="Bedrooms"
                  subjectValue={subject?.readonlyData?.backupListingData?.listing?.BedroomsTotal?.toString()}
                  fieldName="bedrooms"
                  inputType="unit"
                  includeMatchCriteria
                  unitName="bedrooms"
                  includeClearButton
                  onClear={() => clearFilter('bedrooms')}
                />
                <FilterRow
                  label="Baths: Full"
                  subjectValue={subject?.readonlyData?.backupListingData?.listing?.BathroomsFull?.toString()}
                  fieldName="fullBaths"
                  inputType="unit"
                  includeMatchCriteria
                  unitName="baths"
                  includeClearButton
                  onClear={() => clearFilter('fullBaths')}
                />
                <FilterRow
                  label="Stories"
                  subjectValue={subject?.readonlyData?.backupListingData?.listing?.Stories?.toString()}
                  fieldName="stories"
                  inputType="unit"
                  includeMatchCriteria
                  unitName="stories"
                  includeClearButton
                  onClear={() => clearFilter('stories')}
                />
                {/* TODO: Add when supported by Abode */}
                {/* <Row label="Garage Spaces">
                  <StringSelect
                    name="search.matchCriteria.garageSpaces"
                    options={getEnumOptions({
                      name: 'search.matchCriteria.garageSpaces',
                    })}
                    colSpan={1}
                  />
                  <FilterInputFields name="garageSpaces" type="unit" unitName="garage spaces" />
                  <Td colSpan={1}>
                    <Block padding="0 5px">
                      <Typography variant="body1" color="textPrimary">
                        {subject?.editableData?.improvements?.garage?.spaces?.toString() || '-'}
                      </Typography>
                    </Block>
                  </Td>
                </Row> */}
                <FilterRow
                  label="Basement"
                  subjectValue={
                    subject?.readonlyData.backupListingData?.listing.BasementYN ? 'Yes' : 'No'
                  }
                  fieldName="hasBasement"
                  inputType="boolean"
                  includeMatchCriteria={false}
                  includeClearButton={false}
                />
                <FilterRow
                  label="Pool"
                  subjectValue={
                    subject?.readonlyData.backupListingData?.listing.PoolPrivateYN ? 'Yes' : 'No'
                  }
                  fieldName="hasPool"
                  inputType="boolean"
                  includeMatchCriteria={false}
                  includeClearButton={false}
                />
              </Tbody>
            </Table>
          </Block>
          <Flex gap="8px" paddingTop="8px" marginY="20px">
            <Button variant="secondary" text="Close" onClick={handleOnClose} />
            <Button disabled={!allowApplyNewFilters || isLoading} text="Apply" onClick={onSubmit} />
            {isLoading && (
              <Block marginLeft="10px">
                <LoadingScreenSamInline
                  logoWidth="50px"
                  overrideText="Applying filters... this might take a few seconds"
                  overrideComponent={
                    <Typography color="textSecondary">
                      Applying filters... this might take a few seconds
                    </Typography>
                  }
                />
              </Block>
            )}
            {filtersMessage && (
              <Flex flexDirection="row" alignItems="center">
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  width="24px"
                  height="24px"
                  borderRadius="50%"
                  borderColor={filtersMessage.color}
                  borderWidth="2px"
                  marginLeft="10px"
                  marginRight="10px"
                  borderStyle="solid"
                >
                  <Icon color={filtersMessage.color} icon={filtersMessage.icon} size="md" />
                </Flex>
                <Typography color="textSecondary">{filtersMessage.message}</Typography>
              </Flex>
            )}
          </Flex>
          <FilterStatistics statistics={statistics} isLoading={isLoading} />
        </Flex>

        <FilterDrawerMapView subjectComp={subjectComp} />
      </Flex>
    </Drawer>
  );
};
